import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { mergeClasses } from '@fluentui/react-components';
import { SideNavigation } from '@bingads-webui-fluent-component/navigation';
import { handleRealUsedFlag, RealUsedFlagSuffix } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

import { useAdStudioSideNavigation } from './use-ad-studio-side-navigation';
import { FolderPathTreeView } from './components/folders-tree-view';
import { useAdStudioNavigationStyle } from './style';

const MIN_VISIBLE_TAB_COUNT = 8;

const AdStudioNavigationImp = ({
  history,
  i18n,
  rootRoute,
  dataService,
  logger,
  permissions,
  onDropToFolder: onDropToFolderProp,
  adsStudioAccountPreferences,
}) => {
  const {
    groupedItems,
    selectedItemId,
    setSelectedFolderId,
    folderPathTree,
    loadFolderPathTreeItem,
    loadMoreFolderPathTreeItem,
    handleFolderItemClicked,
    onDropToFolder,
    navigateIntoRootFolder,
  } = useAdStudioSideNavigation({
    history,
    rootRoute,
    dataService,
    onDropToFolder: onDropToFolderProp,
    permissions,
    adsStudioAccountPreferences,
  });

  const classes = useAdStudioNavigationStyle();
  const renderFolderPathTree = useCallback(() => (
    folderPathTree && folderPathTree.subFolders.length > 0 &&
    <FolderPathTreeView
      folderPathTree={folderPathTree}
      loadFolderPathTreeItem={loadFolderPathTreeItem}
      loadMoreFolderPathTreeItem={loadMoreFolderPathTreeItem}
      i18n={i18n}
      onItemSelected={handleFolderItemClicked}
      selectedItemId={selectedItemId}
      logger={logger}
      onDrop={onDropToFolder}
    />
  ), [folderPathTree, handleFolderItemClicked, i18n, loadFolderPathTreeItem, loadMoreFolderPathTreeItem, logger, onDropToFolder, selectedItemId]);

  const groupedStaticItems = useMemo(() => groupedItems.map(items => items.map((item) => {
    if (item.id === 'folder-path-tree') {
      return {
        ...item,
        key: item.id,
        text: 'folderPathTree',
        renderCustomizedItem: renderFolderPathTree,
      };
    }
    return (
      {
        id: item.id,
        key: item.id,
        icon: item.icon,
        text: i18n.getString(item.name),
        isSelected: item.id === selectedItemId,
        onClick: () => {
          handleRealUsedFlag(adsStudioAccountPreferences, RealUsedFlagSuffix.App);
          setSelectedFolderId(null);
          navigateIntoRootFolder();
          item.onClick();
          logger.logTrace('AdStudioNavigation: static item clicked', { navigationItem: item.name });
        },
      }
    );
  })), [groupedItems, i18n, selectedItemId, renderFolderPathTree, adsStudioAccountPreferences, setSelectedFolderId, navigateIntoRootFolder, logger]);

  const getSelectedNavigationItemId = useCallback(() => selectedItemId, [selectedItemId]);

  return (
    <>
      <div className={mergeClasses(classes.adStudioNavigation, 'ad-studio-navigation', 'ad-studio-side-navigation')}>
        <SideNavigation
          items={groupedStaticItems}
          className={classes.customTabItem}
          config={{ selectionMode: 2, getSelectedNavigationItemId, minimumVisibleCount: MIN_VISIBLE_TAB_COUNT }}
        />
      </div>
      <div className={mergeClasses(classes.placeholder, 'ad-studio-navigation-placeholder')} />
    </>
  );
};

AdStudioNavigationImp.propTypes = {
  history: PropTypes.shape({
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }),
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  rootRoute: PropTypes.string.isRequired,
  dataService: PropTypes.shape({
    folderPathTree: PropTypes.arrayOf(PropTypes.shape({})),
    loadFolderPathTreeItem: PropTypes.func,
    loadMoreFolderPathTreeItem: PropTypes.func,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  onDropToFolder: PropTypes.func.isRequired,
};

export const AdStudioSideNavigation = AdStudioNavigationImp;
